<template>
  <v-container fluid class="OP950Documents">
    <PaginationController
      v-model="documents"
      getDataActionName="getOP950Pagination"
      :historyLimit="historyLimit"
      :select="hazmatWarehouseSite"
      haveStartDate
      isStartWithoutData
      ref="paginate"
    >
      <template v-slot:dataTable>
        <v-data-table
          :headers="headers"
          :items="documents"
          hide-actions
          item-key="orderId"
        >
          <template v-slot:items="props">
            <td class="text-xs-left">
              <OP950DocumentsWarehouseSiteWidget 
                :info="props.item" 
              />
            </td>
            <td class="text-xs-left">{{ props.item.warehouseName }}</td>
            <td class="text-xs-left">{{ toDateYYYYMMDD(props.item.closeDate) }}</td>
            <td class="text-xs-left">{{ props.item.operatorName }}</td>
            <td class="text-xs-left">{{ toDateYYYYMMDDHHmmss(props.item.createTime) }}</td>
            <td class="text-xs-center">
              <v-layout row>
                <v-flex>
                  <v-btn 
                    dark 
                    flat 
                    color="primary" 
                    @click.stop="downloadFile(props.item.filePathDownloadURL)"
                    :disabled="!props.item.filePath"
                  >{{ $t('export') }}</v-btn>
                </v-flex>
              </v-layout>
            </td>
          </template>
        </v-data-table>
      </template>
      <template v-slot:beforeMenu>
        <v-layout row wrap justify-space-between>
          <v-flex md3>
            <v-autocomplete 
              :items="allHazmatSitesOptions"
              v-model="hazmatWarehouseSite"
              :label="$t('warehouseSite')"
            />
          </v-flex>
          <v-flex md3>
            <v-btn 
              color="primary" 
              dark 
              @click="showOp950Modal"
            >
              {{ $t('createOp950Doc') }}
            </v-btn>          
          </v-flex>
          <v-flex md3>
          </v-flex>
        </v-layout>
      </template>
    </PaginationController>

    <FormSubmitPopup
      ref="form"
      title="OP950"
      v-model="op950Modal.show"
      :rightMethod="exportOp950Doc"
      :rightButtonText="$t('create')"
      :rightButtonLoading="op950Modal.loading"
      :leftMethod="() => op950Modal.show = false"
      hasAlert
      medium>
      <template v-slot:input>
        <v-layout justify-space-between align-baseline>
          <v-flex md4>
            <v-menu
              v-model="showDatePick"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              :nudge-right="40"
              lazy
              full-width
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="op950Modal.closeDate"
                  :label="$t('closeDate')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="op950Modal.closeDate" @input="showDatePick = false"></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex md5>
            <v-autocomplete 
              style="transform: translateY(5px)"
              :items="hazmatSitesOptions"
              v-model="op950Modal.hazmatWarehouseSite"
              :label="$t('warehouseSite')"
            />
          </v-flex>
          <v-flex md1>
          </v-flex>
        </v-layout>
      </template>
    </FormSubmitPopup>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import PaginationController from './PaginationController'
import OP950DocumentsWarehouseSiteWidget from './OP950DocumentsWarehouseSiteWidget'
import FormSubmitPopup from './FormSubmitPopup'
import { timeTools } from '../utils/tools'

export default {
  name: 'OP950Documents',
  mixins: [timeTools],
  components: {
    PaginationController,
    OP950DocumentsWarehouseSiteWidget,
    FormSubmitPopup
  },
  data () {
    return {
      hazmatWarehouseSite: '',
      documents: [],
      historyLimit: 20,
      showDatePick: false,
      op950Modal: {
        show: false,
        loading: false,
        closeDate: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
        hazmatWarehouseSite: ''
      }
    }
  },
  computed: {
    ...mapGetters([
      'hazmatSites',
      'activeWarehouse'
    ]),
    headers () {
      return [
        { text: this.$t('detail'), value: '', align: 'left', sortable: false, width: '8%' },
        { text: this.$t('warehouse'), value: 'warehouseName', align: 'left', sortable: false, width: '5%' },
        { text: this.$t('closeDate'), value: 'closeDate', align: 'left', sortable: false, width: '5%' },
        { text: this.$t('operator'), value: 'operatorName', align: 'left', sortable: false, width: '5%' },
        { text: this.$t('createTime'), value: 'createTime', align: 'left', sortable: false, width: '5%' },
        { text: this.$t('actions'), value: '', align: 'center', sortable: false, width: '5%' }
      ]
    },
    hazmatSitesOptions () {
      return this.hazmatSites.map(site => ({
        text: site.siteName,
        value: site.key
      }))
    },
    allHazmatSitesOptions () {
      if (this.activeWarehouse) return this.hazmatSitesOptions
      const all = { value: '', text: this.$t('all') }
      return [all, ...this.hazmatSitesOptions]
    }
  },
  methods: {
    downloadFile (fullPath, fileName) {
      const link = document.createElement('a')
      link.href = fullPath
      link.style.display = 'none'
      link.download = fileName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async exportOp950Doc () {
      try {
        if (this.loading) return
        this.loading = true
        const { warehouseKey, key } = this.hazmatSites.find(site => site.key === this.op950Modal.hazmatWarehouseSite) || {}
        const { data } = await this.$store.dispatch('exportOP950Document', {
          closeDate: this.op950Modal.closeDate,
          warehouseKey,
          siteKey: key
        })
        this.downloadFile(data.filePathDownloadURL)
        this.op950Modal.show = false
        this.$refs.paginate.$$initiateTable()
      } catch (err) {
        console.log(err)
        alert(err.message || this.$t('sthBroke'))
      } finally {
        this.loading = false
      }
    },
    showOp950Modal () {
      this.op950Modal.show = true
      if (
        !this.op950Modal.hazmatWarehouseSite && 
        this.hazmatSitesOptions.length
      ) {
        this.op950Modal.hazmatWarehouseSite = this.hazmatSitesOptions[0].value
      }
    }
  },
  watch: {
    allHazmatSitesOptions: {
      handler (val, oldVal) {
        const old = oldVal || []
        if (!this.activeWarehouse || old.length || !val.length) return
        this.hazmatWarehouseSite = val[0].value
      },
      immediate: true
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/OP950Documents.vue